<template>
	<section class="package-offer" :aria-labelledby="`${item.id}-title`" :style="spacingStyle">
		<header class="package-offer__header">
			<p v-if="presentation.eyebrow" class="package-offer__eyebrow">{{ presentation.eyebrow }}</p>
			<h2 :id="`${item.id}-title`">{{ presentation.title }}</h2>
			<div
				v-if="presentation.descriptionHtml"
				class="package-offer__description brand-agreement-text"
				v-html="sanitize(presentation.descriptionHtml)" />
		</header>
		<p v-if="decisionWarning" class="package-offer__decision-warning" role="status">{{ decisionWarning }}</p>
					<div
						:class="['package-offer__options', optionLayoutClass, { 'package-offer__invalid': optionsInvalid }]"
						:tabindex="optionsInvalid ? -1 : null"
						:aria-invalid="optionsInvalid ? 'true' : null"
					>
						<article v-for="option in options" :key="option.id" :class="optionClass(option)">
				<span v-if="option.badge" class="package-offer__badge">{{ option.badge }}</span>
							<div class="package-offer__option-heading">
					<h3>{{ option.title }}</h3>
							</div>
				<div
					v-if="option.descriptionHtml"
					class="package-offer__option-description brand-agreement-text"
					v-html="sanitize(option.descriptionHtml)" />
							<div class="package-offer__option-footer">
								<option-price :option="option" />
					<button
						v-if="selectionMode !== 'FIXED' && !readOnly"
						type="button"
						:class="[
							'package-offer__option-action', { 'package-offer__option-action--selected': selectedOptionIds.includes(option.id) },
						]"
						:aria-pressed="selectedOptionIds.includes(option.id)"
						:disabled="optionDisabled(option.id)"
						@click="toggleOption(option.id)"
					>
									{{ option.required ? option.requiredLabel || 'Included' : selectedOptionIds.includes(option.id) ? selectedOptionLabel : chooseOptionLabel }}
					</button>
							</div>
						</article>
					</div>

		<section
			v-if="eligibleAddOns.length"
			:class="['package-offer__section', { 'package-offer__invalid': addOnsInvalid }]"
			:aria-label="presentation.addOnsLabel || $t('agreement-package-offer.add-ons')"
			:aria-invalid="addOnsInvalid ? 'true' : null"
			:tabindex="addOnsInvalid ? -1 : null"
				>
			<h3 v-if="presentation.addOnsLabel">{{ presentation.addOnsLabel }}</h3>

				<div class="package-offer__add-on-list" :class="optionLayoutClass">
					<article v-for="addOn in eligibleAddOns" :key="addOn.id"
					:class="[
						'package-offer__add-on',
						{
							'package-offer__add-on--selected': isAddOnSelected(addOn.id),
							'package-offer__add-on--required': addOn.required,
						},
					]"
					@click="toggleAddOn(addOn)"
				>
					<div class="package-offer__add-on-heading">
							<input :id="addOnInputId(addOn.id)" type="checkbox" :checked="isAddOnSelected(addOn.id)"
							:disabled="readOnly || addOn.required"
							:aria-label="addOn.title"
							@click.stop @change="toggleAddOn(addOn)" />
						<div class="package-offer__add-on-copy">
							<strong>{{ addOn.title }}</strong>
						<div v-if="addOn.descriptionHtml"
								class="package-offer__option-description brand-agreement-text"
								v-html="sanitize(addOn.descriptionHtml)"
							/>
				</div>
					</div>
					<div class="package-offer__add-on-footer">
						<div class="package-offer__price">
							<strong v-if="addOnOneTimePrice(addOn)">{{ labeledPrice(addOnOneTimePrice(addOn), addOn) }}</strong>
							<strong v-for="(price, priceIndex) in addOnRecurringPrices(addOn)" :key="price.id || price.label || priceIndex"
								>{{ labeledPrice(price.amount, addOn) }}</strong
							>
							<strong v-if="addOnDepositPrice(addOn)">{{ labeledPrice(addOnDepositPrice(addOn), addOn) }}</strong>
						</div>
							<input v-if="isAdjustable(addOn) && isAddOnSelected(addOn.id)"
								type="number"
								:value="addOnQuantity(addOn.id)"
								:min="quantityRules(addOn).minimum"
								:max="quantityRules(addOn).maximum"
								:step="quantityRules(addOn).step"
							:aria-label="`${addOn.title} quantity`"
							@click.stop
								@change="setAddOnQuantity(addOn, $event.target.value)"
							/>
					</div>
				</article>
			</div>
				</section>

		<div v-if="quoteError && !decisionWarning" class="package-offer__quote-feedback">
				<div v-if="quoteError" role="alert" class="package-offer__quote-error">
					<p>{{ $t('agreement-package-offer.quote-error') }}</p>
					<button type="button" class="package-offer__retry brand-agreement-button" @click="requestQuote">
					{{ $t('agreement-package-offer.retry') }}
				</button>
				</div>
					</div>
					<section
			v-if="visiblePaymentChoices.length"
			:class="['package-offer__section', 'package-offer__payment-section', { 'package-offer__invalid': paymentChoiceInvalid }]"
			:aria-label="presentation.paymentChoiceLabel || $t('agreement-package-offer.payment-choice')"
			:aria-busy="quoteLoading ? 'true' : 'false'"
			:aria-invalid="paymentChoiceInvalid ? 'true' : null"
			:tabindex="paymentChoiceInvalid ? -1 : null"
		>
			<h3 v-if="presentation.paymentChoiceLabel">{{ presentation.paymentChoiceLabel }}</h3>
			<p v-if="quoteLoading" class="package-offer__quote-status" role="status">
				{{ $t('agreement-package-offer.updating-quote') }}
			</p>
						<div class="package-offer__payment-choices">
				<article
					v-for="choice in visiblePaymentChoices"
					:key="choice.id"
					:class="[
						'package-offer__payment-choice', {
							'package-offer__payment-choice--selected': visiblePaymentChoices.length > 1 && selection.paymentChoiceId === choice.id,
							'package-offer__payment-choice--single': visiblePaymentChoices.length === 1,
						},
					]"
				>
					<button
						type="button"
						:aria-pressed="selection.paymentChoiceId === choice.id"
						:disabled="readOnly"
						@click="setPaymentChoice(choice.id)"
					>
						<span class="package-offer__payment-choice-title">
							<strong>{{ choice.label }}</strong>
							<span v-if="paymentChoiceAmount(choice)" class="package-offer__payment-choice-amount">
								<s v-if="paymentChoiceOriginalAmount(choice)">{{ paymentChoiceOriginalAmount(choice) }}</s>
								<strong>{{ paymentChoiceAmount(choice) }}</strong>
							</span>
						</span>
						<span v-if="choice.description" class="package-offer__payment-choice-description">{{
							choice.description
						}}</span>
					</button>
					<div
						v-if="
							selection.paymentChoiceId === choice.id &&
								(paymentTaxBreakdown.length || paymentChoiceTerms(choice).length)
						"
						class="package-offer__payment-choice-details"
					>
						<div
							v-for="(row, rowIndex) in paymentTaxBreakdown"
							:key="`${row.label}-${rowIndex}`"
							class="package-offer__payment-choice-term"
						>
							<strong>{{ row.label }}</strong
							><strong>{{ row.value }}</strong>
						</div>
						<div v-for="term in paymentChoiceTerms(choice)" :key="term.id" class="package-offer__payment-choice-term">
							<strong>{{ term.label }}</strong
							><strong>{{ term.value }}</strong>
					</div>
						</div>
						</article>
		</div>
					</section>
		<p class="sr-only" aria-live="polite">{{ announcement }}</p>
	</section>
</template>

	<script>
	import { commercialDecisionWarningForState } from '@/modules/agreements/commercialDecision';
	import { clampQuantity, isDecimalAmount, isDecimalZero,
		packageOfferSelection, } from '@/modules/agreements/packageOfferSelection';
	import ResponsiveMixin from './ResponsiveMixin';
	const OptionPrice = {
		functional: true,
		props: ['option'],
		render(h, context) {
			const option = context.props.option;
			const priceSummary = option.priceSummary || {};
			const hourlyHasDeposit = Boolean(priceSummary.hourly && priceSummary.hourly.deposit);
			return h('span', { class: 'package-offer__price' }, [
				priceSummary.oneTime ? h('span', context.parent.labeledPrice(priceSummary.oneTime, option)) : null,
				priceSummary.hourly ? h('span', { class: { 'package-offer__hourly-rate--primary': !hourlyHasDeposit } }, context.parent.labeledPrice(priceSummary.hourly.rate, option)) : null,
				hourlyHasDeposit
					? h('strong', context.parent.labeledPrice(priceSummary.hourly.deposit, { priceLabel: priceSummary.hourly.depositLabel }))
					: null,
				...(priceSummary.recurring || []).map((price, index) => h('span', { key: price.id || price.label || index }, context.parent.labeledPrice(price.amount, option))
				),
				priceSummary.deposit ? h('span', context.parent.labeledPrice(priceSummary.deposit, option)) : null,
			]);
		},
	};

	export default {
		name: 'PackageOffer',
		components: { OptionPrice },
		mixins: [ResponsiveMixin],
		props: {
			tokens: { type: Map, default: () => new Map() },
			item: { type: Object, required: true },
			agreement: { type: Object, required: true },
			viewerService: { type: Object, default: null },
			pageWidth: { type: Number, default: 0 },
		},
		data() {
			const definition = this.item.definition || {};
			const commercialState = this.item.commercialState || {};
			const source = commercialState.selectionLocked ? this.item.acceptedSelection : definition.defaultSelection;
			return {
				selection: this.copySelection(source),
				quote: null,
				quoteLoading: false,
				quoteError: false,
				quoteRequestId: 0,
				quoteTimer: null,
				announcement: '',
				selectionTouched: false,
				submissionAttempted: false,
			};
		},
		computed: {
			definition() { return this.item.definition || {};
			},
			spacingStyle() {
				return {
					paddingTop: `${this.item.pt == null ? 0 : this.item.pt}px`,
					paddingBottom: `${this.item.pb == null ? 0 : this.item.pb}px`,
					paddingLeft: `${this.padding(this.item.pl == null ? 0 : this.item.pl)}px`,
					paddingRight: `${this.padding(this.item.pr == null ? 0 : this.item.pr)}px`,
				}; },
			presentation() { return this.definition.presentation || {}; },
			selectionRules() { return this.definition.selectionRules || {}; },
			options() { return Array.isArray(this.definition.options) ? this.definition.options.filter(Boolean) : []; },
			paymentChoices() { return Array.isArray(this.definition.paymentChoices) ? this.definition.paymentChoices.filter(Boolean) : []; },
			addOns() { return Array.isArray(this.definition.addOns) ? this.definition.addOns.filter(Boolean) : []; },
			selectionMode() { return ['FIXED', 'CHOOSE_ONE', 'CHOOSE_MANY'].includes(this.selectionRules.mode) ? this.selectionRules.mode : 'FIXED'; },
			layout() { return ['CARDS', 'STACKED'].includes(this.presentation.layout) ? this.presentation.layout : 'CARDS'; },
			chooseOptionLabel() { return this.presentation.chooseOptionLabel || 'Choose this option'; },
			selectedOptionLabel() { return this.presentation.selectedOptionLabel || 'Selected'; },
			totalLabel() {
				return this.presentation.totalLabel == null ? this.$t('invoice.details.total') : this.presentation.totalLabel;
			},
			optionLayoutClass() { return `package-offer__options--${this.layout.toLowerCase()}`; },
			selectedOptionIds() { return this.selection.optionIds; },
			commercialState() { return this.item.commercialState || {}; },
			decisionWarning() { return commercialDecisionWarningForState(this.commercialState, this.$t.bind(this)); },
			readOnly() { return this.commercialState.canEditSelection !== true || this.commercialState.selectionLocked === true; },
			eligibleAddOns() { return this.addOns.filter(this.isAddOnEligible); },
			oneTime() { return this.quote && this.quote.oneTime && typeof this.quote.oneTime === 'object' ? this.quote.oneTime : null; },
			dueToday() { return this.quote && this.quote.dueToday && typeof this.quote.dueToday === 'object' ? this.quote.dueToday : null; },
			futureInstallments() { return this.quote && Array.isArray(this.quote.futureInstallments) ? this.quote.futureInstallments : []; },
			quoteValidation() { return this.quote && this.quote.validation && typeof this.quote.validation === 'object' ? this.quote.validation : {}; },
			quoteErrors() { return Array.isArray(this.quoteValidation.errors) ? this.quoteValidation.errors.filter(Boolean) : []; },
			invalidFields() { return new Set(this.quoteErrors.map((error) => error.field).filter(Boolean)); },
			optionsInvalid() {
				if (this.decisionWarning) return false;
				if (this.submissionAttempted) {
					return this.localSelectionInvalid || this.invalidFields.has('optionIds') || this.invalidFields.has('selectionRules.mode');
				}
				return this.selectionTouched && this.localSelectionInvalid;
			},
			addOnsInvalid() { return !this.decisionWarning && this.invalidFields.has('addOns'); },
			paymentChoiceInvalid() { return !this.decisionWarning && this.invalidFields.has('paymentChoiceId'); },
			collection() {
				const source = this.quote && this.quote.collection;
				if (!source || !['PAYMENT_INTENT', 'NONE'].includes(source.mode)) return null;
				return { mode: source.mode, savePaymentMethod: source.savePaymentMethod === true, autoChargeFuture: source.autoChargeFuture === true, };
			},
			paymentApplicabilityKnown() { return Boolean(this.oneTime && isDecimalAmount(this.oneTime.eligibleSubtotal));
			},
			paymentTaxBreakdown() {
				const totals = this.quote && this.quote.paymentChoiceTotals;
				const tax = this.quote && this.quote.taxSummary;
				if (!totals) return [];
				const label = tax && tax.label ? tax.label : this.$t('invoice.details.tax');
				const hasTax = totals.tax && Number(totals.tax.amount) > 0;
				return [
					...(hasTax ? [{ label: tax && tax.rate != null ? `${label} (${tax.rate}%)` : label, value: this.money(totals.tax) }] : []),
					{ label: this.totalLabel, value: this.money(totals.total) },
				]; },
			visiblePaymentChoices() {
				if (this.commercialState.selectionLocked) return this.paymentChoices.filter((choice) => choice.id === this.selection.paymentChoiceId);
				if (!this.paymentApplicabilityKnown) return [];
				const depositDue = this.dueToday && Array.isArray(this.dueToday.lines) && this.dueToday.lines.some((line) => line.category === 'DEPOSIT');
				return isDecimalZero(this.oneTime.eligibleSubtotal) && !depositDue ? [] : this.paymentChoices; },
			selectionMaximum() {
				const raw = this.selectionRules.maximumSelections;
				if (raw == null || (typeof raw === 'string' && !raw.trim())) return null;
				const maximum = Number(raw);
				return Number.isFinite(maximum) && maximum >= 0 ? maximum : null;
			},
			localSelectionInvalid() {
				if (this.selectionMode === 'CHOOSE_ONE') return this.selectedOptionIds.length !== 1;
				if (this.selectionMode !== 'CHOOSE_MANY') return false;
				const minimum = Number(this.selectionRules.minimumSelections || 0);
				return this.selectedOptionIds.length < minimum ||
					(this.selectionMaximum !== null && this.selectedOptionIds.length > this.selectionMaximum);
			},
		},
		created() { this.normalizeLocalSelection(!this.commercialState.selectionLocked); },
		mounted() { this.requestQuote(); },
		beforeDestroy() { clearTimeout(this.quoteTimer); },
		methods: {
			copySelection(source) {
				return packageOfferSelection(source);
			},
			sanitize(html) {
				let rendered = html || '';
				for (const [key, value] of this.tokens) {
					const escaped = String(value == null ? '' : value)
						.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
						.replaceAll('"', '&quot;').replaceAll("'", '&#39;');
					rendered = rendered.replaceAll(`{{${key}}}`, () => escaped);
				}
				return this.$formatters.sanitize(rendered);
			},
			money(value) { return isDecimalAmount(value) ? this.$formatters.dollars(value.amount, true, true, value.currency, 2, 0) : ''; },
			addOnInputId(addOnId) { return `${this.item.id}-add-on-${addOnId}`; },
			optionClass(option) { return { 'package-offer__option': true, 'package-offer__option--selected': this.selectedOptionIds.includes(option.id), 'package-offer__option--required': option.required === true, }; },
			optionDisabled(optionId) {
				const option = this.options.find((candidate) => candidate.id === optionId);
				if (this.readOnly || (option && option.required === true)) return true;
				if (this.selectionMode !== 'CHOOSE_MANY' || this.selectedOptionIds.includes(optionId)) return false;
				return this.selectionMaximum !== null && this.selectedOptionIds.length >= this.selectionMaximum;
			},
			quantityRules(addOn) { return addOn.quantity || {}; },
			isAdjustable(addOn) { return this.quantityRules(addOn).adjustable === true; },
			priceLabel(item) { return item.priceLabel == null ? this.$t('agreement-package-offer.one-time') : item.priceLabel; },
			labeledPrice(amount, item) { return [this.money(amount), this.priceLabel(item)].filter(Boolean).join(' '); },
			paymentChoiceAmount(choice) {
				return this.selection.paymentChoiceId === choice.id && this.quote
					? this.money(this.quote.paymentChoiceTotals && this.quote.paymentChoiceTotals.subtotal)
					: '';
			},
			paymentChoiceOriginalAmount(choice) {
				const discount = this.oneTime && this.oneTime.discount;
				if (
					choice.kind !== 'PAY_IN_FULL' ||
					this.selection.paymentChoiceId !== choice.id ||
					!discount ||
					!(Number(discount.amount) > 0)
				)
					return '';
				return this.money(this.quote.paymentChoiceTotals && this.quote.paymentChoiceTotals.originalSubtotal);
			},
			paymentChoiceTerms(choice) {
				if (choice.kind !== 'INSTALLMENTS' || !this.quote) return [];
				const immediate = (this.dueToday && Array.isArray(this.dueToday.lines) ? this.dueToday.lines : [])
					.filter((line) => line.category === 'ONE_TIME')
					.map((line, index) => ({
						id: line.id || `immediate-${index}`,
						label: line.label || '',
						value: this.money(index === 0 ? this.dueToday.total : line.amount),
					}));
				const future = this.futureInstallments.map((installment, index) => ({
					id: installment.id || `future-${index}`,
					label: installment.label || '',
					value: this.money(installment.total || installment.amount),
				}));
				return [...immediate, ...future].filter((term) => term.value);
			},
			addOnOneTimePrice(addOn) { return addOn.priceSummary && addOn.priceSummary.oneTime ? addOn.priceSummary.oneTime : null; },
			addOnDepositPrice(addOn) { return addOn.priceSummary && addOn.priceSummary.deposit ? addOn.priceSummary.deposit : null; },
			addOnRecurringPrices(addOn) { return addOn.priceSummary && Array.isArray(addOn.priceSummary.recurring) ? addOn.priceSummary.recurring : []; },
			isAddOnEligible(addOn) {
				if (!addOn || !addOn.availability) return false;
				return ( addOn.availability.scope === 'ALL_OPTIONS' || (addOn.availability.scope === 'SPECIFIC_OPTIONS' && (addOn.availability.optionIds || []).some((id) => this.selectedOptionIds.includes(id)))
				);
			},
			isAddOnSelected(id) { return this.selection.addOns.some((addOn) => addOn.addOnId === id); },
			addOnQuantity(id) { const selected = this.selection.addOns.find((addOn) => addOn.addOnId === id); return selected ? selected.quantity : null; },
			normalizeLocalSelection(useDefaults = true) {
				const optionIds = new Set(this.options.map((option) => option.id));
				const defaults = packageOfferSelection(this.definition.defaultSelection).optionIds.filter((id) =>
					optionIds.has(id)
				);
				let selected = [...new Set(this.selection.optionIds.filter((id) => optionIds.has(id)))];
				if (useDefaults && !selected.length) selected = defaults;
				if (this.selectionMode === 'FIXED') selected = [...optionIds];
				if (this.selectionMode === 'CHOOSE_ONE') selected = selected.slice(0, 1);
				if (this.selectionMode === 'CHOOSE_MANY') {
					const required = this.options.filter((option) => option.required === true).map((option) => option.id);
					selected = [...new Set([...required, ...selected])];
					if (this.selectionMaximum !== null) selected = selected.slice(0, this.selectionMaximum);
				}
				this.selection.optionIds = selected;

				const addOns = new Map(this.addOns.map((addOn) => [addOn.id, addOn]));
				this.selection.addOns = this.selection.addOns.reduce((result, selectedAddOn) => {
					const addOn = addOns.get(selectedAddOn.addOnId);
					if (!this.isAddOnEligible(addOn)) return result;
					const quantity = this.isAdjustable(addOn) ? clampQuantity(selectedAddOn.quantity, this.quantityRules(addOn)) : clampQuantity(this.quantityRules(addOn).defaultValue, this.quantityRules(addOn));
					result.push({ addOnId: addOn.id, quantity });
					return result;
				}, []);
				this.includeRequiredAddOns();

				if (!this.paymentChoices.some((choice) => choice.id === this.selection.paymentChoiceId)) {
					const fallback = this.paymentChoices.find((choice) => choice.isDefault) || this.paymentChoices[0];
					this.selection.paymentChoiceId = fallback ? fallback.id : null;
				}
			},
			includeRequiredAddOns() {
				this.eligibleAddOns
					.filter((addOn) => addOn.required === true && !this.isAddOnSelected(addOn.id))
					.forEach((addOn) => {
						this.selection.addOns.push({
							addOnId: addOn.id,
							quantity: clampQuantity(this.quantityRules(addOn).defaultValue, this.quantityRules(addOn)),
						});
					});
			},
			toggleOption(id) {
				if (this.readOnly || !this.options.some((option) => option.id === id) || this.options.some((option) => option.id === id && option.required === true)) return;
				this.selectionTouched = true;
				if (this.selectionMode === 'CHOOSE_ONE') this.selection.optionIds = [id];
				else if (this.selectedOptionIds.includes(id)) this.selection.optionIds = this.selectedOptionIds.filter((optionId) => optionId !== id);
				else if (!this.optionDisabled(id)) this.selection.optionIds = [...this.selectedOptionIds, id];
				else {
					this.announcement = this.$t('agreement-package-offer.maximum-selection-reached', { maximum: this.selectionRules.maximumSelections, });
					return;
				}
				const definitions = this.addOns;
				const removed = this.selection.addOns.filter(
					(selected) => !this.isAddOnEligible(definitions.find((addOn) => addOn.id === selected.addOnId))
				);
				this.selection.addOns = this.selection.addOns.filter((selected) => !removed.includes(selected));
				this.includeRequiredAddOns();
				if (removed.length) this.announcement = this.$t('agreement-package-offer.add-ons-removed', { count: removed.length });
				this.scheduleQuote();
			},
			toggleAddOn(addOn) {
				if (this.readOnly || addOn.required === true || !this.isAddOnEligible(addOn)) return;
				if (this.isAddOnSelected(addOn.id)) this.selection.addOns = this.selection.addOns.filter((selected) => selected.addOnId !== addOn.id);
				else this.selection.addOns.push({ addOnId: addOn.id, quantity: clampQuantity(this.quantityRules(addOn).defaultValue, this.quantityRules(addOn)), });
				this.scheduleQuote();
			},
			setAddOnQuantity(addOn, value) {
				const selected = this.selection.addOns.find((entry) => entry.addOnId === addOn.id);
				if (!selected || this.readOnly) return;
				const quantity = clampQuantity(value, this.quantityRules(addOn));
				selected.quantity = quantity;
				this.announcement = this.$t('agreement-package-offer.quantity-set', { quantity, name: addOn.title });
				this.scheduleQuote();
			},
			setPaymentChoice(id) { if (!this.readOnly && this.paymentChoices.some((choice) => choice.id === id)) { this.selection.paymentChoiceId = id; this.scheduleQuote(); } },
			focusFirstInvalid() {
				this.$nextTick(() => {
					const target = this.$el.querySelector('.package-offer__invalid');
					if (!target) return;
					if (typeof target.focus === 'function') target.focus({ preventScroll: true });
					if (typeof target.scrollIntoView === 'function') target.scrollIntoView({ behavior: 'smooth', block: 'center' });
				});
			},
			scheduleQuote() { clearTimeout(this.quoteTimer); this.quoteTimer = setTimeout(this.requestQuote, 250); },
			async requestQuote() {
				if (!this.viewerService) return;
				const requestId = ++this.quoteRequestId;
				this.quoteLoading = true;
				this.quoteError = false;
				this.$emit('quote-state', { id: this.item.id, ready: false, loading: true });
				try {
					const response = await this.viewerService.quotePackageOffer(this.copySelection(this.selection));
					if (requestId !== this.quoteRequestId) return;
					if (!response || !response.data || !response.data.normalizedSelection) throw new Error('Invalid Package Offer Quote');
					const previousAddOns = this.selection.addOns.map((addOn) => addOn.addOnId);
					this.quote = response.data;
					this.selection = this.copySelection(this.quote.normalizedSelection);
					this.normalizeLocalSelection(false);
					const removedCount = previousAddOns.filter(
						(id) => !this.selection.addOns.some((addOn) => addOn.addOnId === id)
					).length;
					if (removedCount) this.announcement = this.$t('agreement-package-offer.add-ons-removed', { count: removedCount });
					this.$emit('quote-state', { id: this.item.id, ready: this.quoteValidation.valid === true && !this.localSelectionInvalid && Boolean(this.collection), loading: false,
						collection: this.collection,
						dueToday: this.dueToday,
						quote: this.quote,
						paymentChoiceLabel:
							(this.paymentChoices.find((choice) => choice.id === this.selection.paymentChoiceId) || {}).label ||
							'', });
				} catch (error) {
					if (requestId === this.quoteRequestId) {
						this.quoteError = true;
						this.$emit('quote-state', { id: this.item.id, ready: false, loading: false });
					}
				} finally {
					if (requestId === this.quoteRequestId) this.quoteLoading = false;
				}
			},
			finalize() {
				this.submissionAttempted = true;
				if (
					this.quoteLoading || this.quoteError || !this.quote || this.quoteValidation.valid !== true || this.localSelectionInvalid ||
					!this.collection
				) {
					this.announcement = this.quoteLoading ? this.$t('agreement-package-offer.updating-quote') : this.$t('agreement-package-offer.quote-error');
					this.focusFirstInvalid();
					throw new Error(this.announcement);
				}
				return { selection: this.copySelection(this.quote.normalizedSelection), quoteFingerprint: this.quote.quoteFingerprint, collection: this.collection, };
			},
		},
	};
</script>

<style scoped lang="scss">
	.package-offer { font-family: var(--brand-body-family, inherit); font-size: var(--brand-body-size, inherit); font-weight: var(--brand-body-weight, inherit); overflow-wrap: anywhere; color: var(--brand-text, var(--v-gray_90-base)); }
	.package-offer__header { margin-bottom: 24px; }
	.package-offer__eyebrow { margin: 0 0 6px; color: var(--brand-primary, var(--v-primary-base)); font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; }
	.package-offer__header h2 {
		margin: 4px 0 0;
		font-size: clamp(24px, 4vw, 34px);
		line-height: 1.15; }
	.package-offer__description {
		color: var(--brand-muted, var(--v-gray_70-base));
		line-height: 1.6; }
	.package-offer__guidance { color: var(--brand-muted, var(--v-gray_70-base)); }
	.package-offer__decision-warning {
		margin: 0 0 24px;
		padding: 12px 14px;
		border: 1px solid var(--v-warning-base);
		border-radius: 8px;
		background: rgba(255, 193, 7, 0.12);
		font-size: 14px;
		line-height: 1.45;
	}
	.package-offer__options, .package-offer__add-on-list { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr)); gap: 18px 12px;
	}
	.package-offer__options { margin: 24px 0; }
	.package-offer__options--stacked { grid-template-columns: 1fr; }
	.package-offer__option { position: relative; display: flex; flex-direction: column; min-width: 0; padding: 18px; border: 1px solid var(--brand-border, var(--v-gray_30-base)); border-radius: var(--brand-surface-radius, 12px); background: var(--brand-surface, var(--v-white-base)); }
	.package-offer__option--selected { border-color: var(--brand-primary, var(--v-primary-base)); box-shadow: inset 0 0 0 1px var(--brand-primary, var(--v-primary-base)); }
	.package-offer__option-heading { display: flex;
		align-items: center;
		justify-content: space-between; gap: 12px; }
	.package-offer__option-heading h3 {
		margin: 0;
		font-size: 17px; }
	.package-offer__option-description {
		margin: 8px 0;
		color: var(--brand-muted, var(--v-gray_70-base));
		font-size: 13px;
		line-height: 1.5;
	}
	.package-offer__option-description > :first-child {
		margin-top: 0; }
	.package-offer__option-description > :last-child {
		margin-bottom: 0;
	}
	.package-offer__description ul,
	.package-offer__description ol,
	.package-offer__option-description ul,
	.package-offer__option-description ol {
		margin: 1em 0;
		padding-inline-start: 40px;
		list-style-position: outside; }
	.package-offer__option-footer { margin-top: auto; padding-top: 12px; }
	.package-offer__option-action { display: block;
		width: 100%; margin-top: 12px; padding: var(--brand-button-paddingY, 8px) var(--brand-button-paddingX, 10px); border: var(--brand-button-borderWidth, 1px) solid var(--brand-button-secondaryBorder, var(--brand-primary, var(--v-primary-base))); border-radius: var(--brand-button-radius, 8px); background: var(--brand-button-secondaryBackground, transparent); color: var(--brand-button-secondaryText, var(--brand-primary, var(--v-primary-base))); font-family: var(--brand-button-family, inherit); font-size: var(--brand-button-size, inherit); font-weight: var(--brand-button-weight, 700); text-align: center; cursor: pointer; }
	.package-offer__option-action--selected { background: var(--brand-button-fill, var(--brand-primary, var(--v-primary-base))); color: var(--brand-button-text, var(--v-white-base)); }
	.package-offer__option--required .package-offer__option-action:disabled { opacity: 1; cursor: default; }
	.package-offer__badge {
		position: absolute;
		z-index: 1;
		top: 0;
		left: 18px; padding: 2px 8px;
		transform: translateY(-50%);
		border: 1px solid var(--brand-border, var(--v-gray_30-base)); border-radius: 999px; background: var(--brand-surface, var(--v-white-base)); font-size: 12px; font-weight: 700;
	}
	.package-offer__option--selected .package-offer__badge {
		border-color: var(--brand-primary, var(--v-primary-base));
		color: var(--brand-primary, var(--v-primary-base)); }
	.package-offer__price { display: flex; flex-direction: column; gap: 2px; }
	.package-offer__hourly-rate--primary { font-weight: 700; }
	.package-offer__section { margin-top: 20px;
	}
	.package-offer__section > h3 {
		margin: 0 0 9px;
		font-size: 14px;
	}
	.package-offer__add-on-list.package-offer__options--cards .package-offer__add-on { min-width: 0; flex-direction: column; gap: 12px; }
	.package-offer__add-on-list.package-offer__options--cards .package-offer__add-on-footer { min-width: 0; align-items: flex-start; padding-left: 25px; text-align: left; }
	.package-offer__add-on {
		display: flex;
		align-items: stretch;
		justify-content: space-between;
		gap: 18px; padding: 14px;
		border: 1px solid var(--brand-border, var(--v-gray_30-base));
		border-radius: var(--brand-surface-radius, 10px);
		background: var(--brand-surface, var(--v-white-base));
		cursor: pointer; }
	.package-offer__add-on--required {
		cursor: default;
	}
	.package-offer__add-on--selected {
		border-color: var(--brand-primary, var(--v-primary-base));
		box-shadow: inset 0 0 0 1px var(--brand-primary, var(--v-primary-base)); }
	.package-offer__add-on-heading { display: flex;
		min-width: 0;
		flex: 1;
		align-items: flex-start; gap: 10px;
	}
	.package-offer__add-on-heading > input {
		flex: 0 0 auto;
		margin-top: 4px;
	}
	.package-offer__add-on-copy {
		display: block;
		min-width: 0;
	}
	.package-offer__add-on-copy > strong {
		display: block;
		line-height: 1.35;
	}
	.package-offer__add-on-copy .package-offer__option-description {
		margin: 5px 0 0;
	}
	.package-offer__add-on-footer {
		display: flex;
		min-width: max-content;
		flex-direction: column; align-items: flex-end;
		justify-content: flex-end;
		gap: 8px;
		text-align: right;
	}
	.package-offer__add-on-footer > input {
		width: 80px; padding: 6px;
		border: 1px solid var(--brand-field-border, var(--v-gray_40-base));
		border-radius: 7px;
		color: inherit; }
	.package-offer input { accent-color: var(--brand-primary, var(--v-primary-base)); }
	.package-offer input:focus-visible, .package-offer button:focus-visible { outline: 3px solid var(--brand-primary, var(--v-primary-base)); outline-offset: 3px; }
	.package-offer__payment-section {
		position: relative;
	}
	.package-offer__quote-status {
		position: absolute;
		z-index: 1;
		top: -2px;
		right: 0; display: flex; align-items: center; gap: 6px; margin: 0;
		padding: 3px 7px;
		border-radius: 999px;
		background: var(--brand-surface, var(--v-white-base));
		color: var(--brand-muted, var(--v-gray_70-base));
		font-size: 11px;
		line-height: 1; }
	.package-offer__quote-status::before { width: 14px;
		height: 14px; border: 2px solid var(--brand-border, var(--v-gray_30-base));
		border-top-color: var(--brand-primary, var(--v-primary-base)); border-radius: 50%;
		animation: package-offer-spin 0.7s linear infinite;
		content: ''; }
	@keyframes package-offer-spin {
		to {
			transform: rotate(360deg);
		}
	}
	@media (prefers-reduced-motion: reduce) {
		.package-offer__quote-status::before {
			animation: none;
		}
	}
	.package-offer__quote-feedback {
		margin-top: 20px;
	} .package-offer__quote-error, .package-offer__warnings, .package-offer__validation { margin-bottom: 16px; padding: 10px 12px; border-radius: 8px; }
	.package-offer__quote-error { background: var(--v-error_10-base); color: var(--brand-state-errorText, var(--v-error-base)); }
	.package-offer__quote-error p { margin: 4px 0 0; }
	.package-offer__invalid { outline: 2px solid var(--brand-state-errorBorder, var(--v-error-base)); outline-offset: 4px; border-radius: var(--brand-surface-radius, 10px); }
	.package-offer__payment-choices {
		display: grid;
		gap: 8px; }
	.package-offer__payment-choice {
		overflow: hidden; border: 1px solid var(--brand-border, var(--v-gray_30-base)); border-radius: var(--brand-surface-radius, 9px);
		background: var(--brand-surface, var(--v-white-base)); }
	.package-offer__payment-choice--selected {
		border-color: var(--brand-primary, var(--v-primary-base));
	}
	.package-offer__payment-choice--single > button { cursor: default; }
	.package-offer__payment-choice > button {
		display: flex;
		width: 100%;
		flex-direction: column;
		align-items: flex-start;
		padding: 10px 12px;
		border: 0;
		background: transparent;
		color: inherit;
		text-align: left;
		cursor: pointer;
	}
	.package-offer__payment-choice-title {
		display: flex;
		width: 100%;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
	}
	.package-offer__payment-choice-description {
		color: var(--brand-muted, var(--v-gray_70-base)); font-size: 12px; }
	.package-offer__payment-choice-amount {
		display: flex;
		flex: 0 0 auto;
		align-items: baseline;
		gap: 7px;
	}
	.package-offer__payment-choice-amount s {
		color: var(--brand-muted, var(--v-gray_70-base));
		font-weight: 500;
	}
	.package-offer__payment-choice-details {
		padding: 0 12px 10px;
	}
	.package-offer__payment-choice-term {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		padding: 8px 0;
		border-top: 1px solid var(--brand-border, var(--v-gray_30-base));
		font-size: 13px; }
	.package-offer__retry { min-height: 40px; padding: 8px 14px; border: 1px solid currentColor; border-radius: 8px; }
	// Agreement-owned tokens only; the document host excludes the portal theme.
	.brand-agreement .package-offer {
		.package-offer__header h2 { font-family: var(--brand-h1-family); font-size: var(--brand-h1-size); font-weight: var(--brand-h1-weight); }
		.package-offer__option-heading h3,
		.package-offer__section > h3 { font-family: var(--brand-h3-family); font-size: var(--brand-h3-size); font-weight: var(--brand-h3-weight); }
		.package-offer__option,
		.package-offer__payment-choice {
			--brand-text: var(--brand-surfaceText);
			--brand-muted: var(--brand-surfaceMuted);
			color: var(--brand-text);
		}
		.package-offer__option,
		.package-offer__add-on,
		.package-offer__payment-choice {
			border-color: var(--brand-border);
			border-radius: var(--brand-surface-radius);
		}
		.package-offer__option { border-width: var(--brand-choice-borderWidth);
			background: var(--brand-surface); }
		.package-offer__option--selected { border-color: var(--brand-choice-selectedBorder); box-shadow: inset 0 0 0 1px var(--brand-choice-selectedBorder); }
		.package-offer__add-on--selected,
		.package-offer__payment-choice--selected {
			border-color: var(--brand-choice-selectedBorder);
			box-shadow: inset 0 0 0 1px var(--brand-choice-selectedBorder);
		}
		.package-offer__add-on-heading { gap: var(--brand-choice-markerGap); }
		input[type='checkbox'], input[type='radio'] { width: var(--brand-choice-markerSize); height: var(--brand-choice-markerSize); flex-shrink: 0; accent-color: var(--brand-choice-selectedBorder); }
		input:disabled,
		button:disabled { opacity: var(--brand-disabled-opacity); cursor: default; }
		.package-offer__add-on-footer > input { border-radius: var(--brand-field-radius); border-width: var(--brand-field-borderWidth); background: var(--brand-field-fill); color: var(--brand-answer-color); font-family: var(--brand-answer-family); font-size: var(--brand-answer-size); font-weight: var(--brand-answer-weight); padding: var(--brand-field-paddingY) var(--brand-field-paddingX); box-shadow: var(--brand-field-shadow); }
		input:focus-visible, button:focus-visible { outline: max(2px, var(--brand-state-focusRingWidth)) solid var(--brand-state-focusRing); outline-offset: 3px; }
		.package-offer__retry:hover { background: var(--brand-state-hoverBackground) !important; color: var(--brand-state-hoverText) !important; }
		.package-offer__badge, .package-offer__quote-status { background: var(--brand-surface); color: var(--brand-surfaceText); border: 1px solid var(--brand-border); }
		.package-offer__guidance,
		.package-offer__payment-choice-description { font-family: var(--brand-help-family); font-size: var(--brand-help-size); font-weight: var(--brand-help-weight); }
	}
	.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }

	@media (max-width: 760px) {
		.package-offer__options, .package-offer__add-on-list { grid-template-columns: 1fr; }
		.package-offer__add-on {
			flex-direction: column;
			gap: 12px; }
		.package-offer__add-on-footer { align-items: flex-start;
			padding-left: 25px;
			text-align: left; }
	}

	@media print {
		.package-offer__retry { display: none; }
	}
</style>
